/**
 * Hands a result to whatever the device uses for sharing.
 *
 * `navigator.share` is the good path: it opens the system sheet, so the
 * message lands in whichever chat the group already uses. It is missing on
 * desktop Firefox and in every non-secure context, and it rejects when the
 * user dismisses the sheet — a dismissal is not a failure, and must not
 * trigger the fallback, or closing the sheet would silently copy instead.
 *
 * Falling back to the clipboard means the same text is still one paste away.
 */
import { copyText } from './clipboard.js';

export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

export interface SharePayload {
  title: string;
  text: string;
  url?: string;
}

export function canShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export async function share(payload: SharePayload): Promise<ShareOutcome> {
  if (canShare()) {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (error) {
      // AbortError is the user closing the sheet. Anything else — a share
      // target that refused, a permission problem — is worth the fallback.
      if (error instanceof DOMException && error.name === 'AbortError') return 'dismissed';
    }
  }

  const text = payload.url ? `${payload.text}\n${payload.url}` : payload.text;
  return (await copyText(text)) ? 'copied' : 'failed';
}
